import { useEffect, useRef, useState } from 'react'

const MORE_CLASS =
  'portfolio-chart-tab-more absolute z-20 inline-flex size-5 cursor-pointer items-center justify-center rounded-pill border-[0.5px] border-line bg-elevated text-dim shadow-(--shadow-sm) transition-colors hover:bg-[color-mix(in_oklab,var(--color-ink)_10%,var(--color-elevated))] hover:text-ink active:bg-[color-mix(in_oklab,var(--color-ink)_14%,var(--color-elevated))]'

const CARET_LEFT =
  'M165.66,202.34a8,8,0,0,1-11.32,0l-80-80a8,8,0,0,1,0-11.32l80-80a8,8,0,0,1,11.32,11.32L91.31,128l74.35,74.34A8,8,0,0,1,165.66,202.34Z'
const CARET_RIGHT =
  'M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z'

function ScrollChevron({ side, onClick, rightOffset }) {
  return (
    <button
      type="button"
      aria-hidden="true"
      tabIndex={-1}
      className={MORE_CLASS}
      style={side === 'left' ? { left: 0 } : { right: rightOffset }}
      onClick={onClick}
    >
      <span
        data-icon="true"
        className="flex shrink-0 items-center justify-center size-[11px]"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 256 256"
          fill="currentColor"
          className="size-full"
        >
          <path d={side === 'left' ? CARET_LEFT : CARET_RIGHT} />
        </svg>
      </span>
    </button>
  )
}

export default function ChartTabScroller({
  children,
  scrollerClassName = '-mx-2 -my-2 no-scrollbar min-w-0 overflow-x-auto px-2 py-2',
  rightOffset = '-11px',
}) {
  const scrollerRef = useRef(null)
  const [fade, setFade] = useState({ l: '0px', r: '0px' })
  const [canLeft, setCanLeft] = useState(false)
  const [canRight, setCanRight] = useState(false)

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return

    const mq = window.matchMedia('(max-width: 767px)')

    const update = () => {
      if (!mq.matches) {
        setFade({ l: '0px', r: '0px' })
        setCanLeft(false)
        setCanRight(false)
        return
      }

      const max = el.scrollWidth - el.clientWidth
      const left = el.scrollLeft
      const nextLeft = left > 1
      const nextRight = max > 1 && left < max - 1
      setFade({
        l: nextLeft ? '24px' : '0px',
        r: nextRight ? '24px' : '0px',
      })
      setCanLeft(nextLeft)
      setCanRight(nextRight)
    }

    update()
    el.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    const observer = new ResizeObserver(update)
    observer.observe(el)
    mq.addEventListener('change', update)
    document.fonts?.ready?.then(update)

    return () => {
      el.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      observer.disconnect()
      mq.removeEventListener('change', update)
    }
  }, [])

  const scrollByPage = (direction) => {
    const el = scrollerRef.current
    if (!el) return
    const delta = Math.max(80, el.clientWidth * 0.65)
    el.scrollBy({ left: direction * delta, behavior: 'smooth' })
  }

  return (
    <div className="relative min-w-0 flex-1">
      <div
        ref={scrollerRef}
        data-fade="x"
        className={scrollerClassName}
        style={{ '--fade-l': fade.l, '--fade-r': fade.r }}
      >
        <div className="flex items-stretch gap-1">{children}</div>
      </div>
      {canLeft ? (
        <ScrollChevron side="left" onClick={() => scrollByPage(-1)} />
      ) : null}
      {canRight ? (
        <ScrollChevron side="right" rightOffset={rightOffset} onClick={() => scrollByPage(1)} />
      ) : null}
    </div>
  )
}
